<template>
  <Teleport to="body">
    <div v-if="isOpen" class="command-palette-backdrop" @mousedown.self="close">
      <div class="command-palette" role="dialog" aria-label="Command palette">
        <div class="command-palette-input-wrapper">
          <font-awesome-icon
            icon="search"
            class="command-palette-search-icon"
          />
          <input
            ref="inputRef"
            v-model="query"
            class="command-palette-input"
            placeholder="Type a command..."
            role="combobox"
            aria-expanded="true"
            aria-controls="command-palette-listbox"
            :aria-activedescendant="activeItemId"
            aria-autocomplete="list"
            aria-label="Search commands"
            @keydown="handleKeydown"
          />
          <kbd class="command-palette-kbd">ESC</kbd>
        </div>

        <div
          v-if="groups.length > 0"
          id="command-palette-listbox"
          role="listbox"
          aria-label="Commands"
          class="command-palette-results"
        >
          <div v-for="group in groups" :key="group.label" role="presentation">
            <div class="command-palette-group-label" role="presentation">
              {{ group.label }}
            </div>
            <div
              v-for="command in group.commands"
              :id="`command-item-${command.id}`"
              :key="command.id"
              role="option"
              :aria-selected="command.id === activeCommandId"
              class="command-palette-item"
              :class="{
                'command-palette-item-active': command.id === activeCommandId,
              }"
              @mouseenter="setActiveCommand(command.id)"
              @click="executeCommand(command)"
            >
              <font-awesome-icon
                v-if="command.icon"
                :icon="command.icon"
                class="command-palette-item-icon"
              />
              <span>{{ command.label }}</span>
            </div>
          </div>
        </div>

        <div v-else class="command-palette-empty">No commands found</div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import {
  useCommandPalette,
  type Command,
} from "@/composables/useCommandPalette";
import { useEventListener } from "@vueuse/core";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";

const {
  isOpen,
  close,
  toggle,
  searchCommands,
  executeCommand: execCmd,
} = useCommandPalette();

const inputRef = useTemplateRef<HTMLInputElement>("inputRef");
const query = ref("");
const activeCommandId = ref<string | null>(null);

const groups = computed(() => searchCommands(query.value));

const flatCommands = computed(() => groups.value.flatMap((g) => g.commands));

const activeItemId = computed(() =>
  activeCommandId.value ? `command-item-${activeCommandId.value}` : undefined
);

function setActiveCommand(id: string) {
  activeCommandId.value = id;
}

function executeCommand(command: Command) {
  query.value = "";
  activeCommandId.value = null;
  execCmd(command);
}

// Reset active index when query or results change
watch(flatCommands, (cmds) => {
  if (cmds.length > 0) {
    activeCommandId.value = cmds[0].id;
  } else {
    activeCommandId.value = null;
  }
});

// Focus input when opened, reset query when closed
watch(isOpen, (open) => {
  if (open) {
    query.value = "";
    nextTick(() => {
      inputRef.value?.focus();
    });
  }
});

function handleKeydown(e: KeyboardEvent) {
  const cmds = flatCommands.value;
  if (cmds.length === 0) return;

  const currentIndex = cmds.findIndex((c) => c.id === activeCommandId.value);

  switch (e.key) {
    case "ArrowDown": {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % cmds.length;
      activeCommandId.value = cmds[nextIndex].id;
      scrollActiveIntoView();
      break;
    }
    case "ArrowUp": {
      e.preventDefault();
      const prevIndex = (currentIndex - 1 + cmds.length) % cmds.length;
      activeCommandId.value = cmds[prevIndex].id;
      scrollActiveIntoView();
      break;
    }
    case "Enter": {
      e.preventDefault();
      const active = cmds.find((c) => c.id === activeCommandId.value);
      if (active) {
        executeCommand(active);
      }
      break;
    }
    case "Escape": {
      e.preventDefault();
      close();
      break;
    }
  }
}

function scrollActiveIntoView() {
  nextTick(() => {
    const el = document.querySelector(".command-palette-item-active");
    el?.scrollIntoView({ block: "nearest" });
  });
}

// Global Cmd+K / Ctrl+K listener
function handleGlobalKeydown(e: KeyboardEvent) {
  if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
    e.preventDefault();
    toggle();
  }
}

useEventListener(window, "keydown", handleGlobalKeydown);
</script>

<style scoped lang="scss">
.command-palette-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 20vh;
}

.command-palette {
  width: 100%;
  max-width: 560px;
  background: var(--bs-body-bg, #fff);
  border-radius: 12px;
  box-shadow: 0 16px 70px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.command-palette-input-wrapper {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--bs-border-color, #dee2e6);
  gap: 10px;
}

.command-palette-search-icon {
  color: var(--bs-secondary-color, #6c757d);
  font-size: 14px;
  flex-shrink: 0;
}

.command-palette-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  background: transparent;
  color: var(--bs-body-color, #212529);

  &::placeholder {
    color: var(--bs-secondary-color, #6c757d);
  }
}

.command-palette-kbd {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid var(--bs-border-color, #dee2e6);
  color: var(--bs-secondary-color, #6c757d);
  background: var(--bs-secondary-bg, #e9ecef);
  flex-shrink: 0;
}

.command-palette-results {
  max-height: 360px;
  overflow-y: auto;
  padding: 8px 0;
}

.command-palette-group-label {
  padding: 8px 16px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--bs-secondary-color, #6c757d);
}

.command-palette-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  color: var(--bs-body-color, #212529);

  &:hover,
  &.command-palette-item-active {
    background-color: var(--phoenix-primary);
    color: #fff;

    .command-palette-item-icon {
      color: #fff;
    }
  }
}

.command-palette-item-icon {
  font-size: 14px;
  color: var(--bs-secondary-color, #6c757d);
  width: 16px;
  text-align: center;
}

.command-palette-empty {
  padding: 24px 16px;
  text-align: center;
  color: var(--bs-secondary-color, #6c757d);
  font-size: 14px;
}
</style>
